import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  Box,
  Button,
  Card,
  CardContent,
  CardActions,
  Grid,
  Typography,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Chip,
} from "@mui/material";
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Warehouse as WarehouseIcon,
  Analytics as AnalyticsIcon,
} from "@mui/icons-material";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { setWarehouses, setCurrentWarehouse } from "../../store/warehouseSlice";
import * as warehouseApi from "./api";
import Header from "../../components/Header";

const WarehouseList = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const warehouses = useSelector((state) => state.warehouse.warehouses);

  const [openDialog, setOpenDialog] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    dimensions: {
      width: 100,
      height: 100,
      unit: "meters",
    },
    gridCellSize: 1,
    address: {
      street: "",
      city: "",
      postalCode: "",
      country: "Germany",
    },
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchWarehouses();
  }, []);

  const fetchWarehouses = async () => {
    try {
      setLoading(true);
      const response = await warehouseApi.getAllWarehouses();
      if (response.success) {
        dispatch(setWarehouses(response.data));
      }
    } catch (error) {
      console.error("Error fetching warehouses:", error);
      toast.error("Fehler beim Laden der Lager");
    } finally {
      setLoading(false);
    }
  };

  const handleCreateWarehouse = async () => {
    try {
      setLoading(true);
      const response = await warehouseApi.createWarehouse(formData);
      if (response.success) {
        toast.success("Lager erfolgreich erstellt");
        setOpenDialog(false);
        fetchWarehouses();
        // Reset form
        setFormData({
          name: "",
          description: "",
          dimensions: {
            width: 100,
            height: 100,
            unit: "meters",
          },
          gridCellSize: 1,
          address: {
            street: "",
            city: "",
            postalCode: "",
            country: "Germany",
          },
        });
      }
    } catch (error) {
      console.error("Error creating warehouse:", error);
      toast.error(error.message || "Fehler beim Erstellen des Lagers");
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteWarehouse = async (id, name) => {
    if (window.confirm(`Möchten Sie das Lager "${name}" wirklich löschen?`)) {
      try {
        const response = await warehouseApi.deleteWarehouse(id);
        if (response.success) {
          toast.success("Lager erfolgreich gelöscht");
          fetchWarehouses();
        }
      } catch (error) {
        console.error("Error deleting warehouse:", error);
        toast.error(error.message || "Fehler beim Löschen des Lagers");
      }
    }
  };

  const handleOpenDesigner = (warehouse) => {
    dispatch(setCurrentWarehouse(warehouse));
    navigate(`/warehouse/${warehouse._id}/designer`);
  };

  const handleViewAnalytics = (warehouse) => {
    dispatch(setCurrentWarehouse(warehouse));
    navigate(`/warehouse/${warehouse._id}/analytics`);
  };

  return (
    <Box m="20px">
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Header title="LAGERVERWALTUNG" subtitle="Verwalten Sie Ihre Lager und Layouts" />
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setOpenDialog(true)}
          sx={{
            backgroundColor: "#4361ee",
            color: "white",
            "&:hover": {
              backgroundColor: "#3a0ca3",
            },
          }}
        >
          Lager erstellen
        </Button>
      </Box>

      {loading && warehouses.length === 0 ? (
        <Typography>Lade Lager...</Typography>
      ) : warehouses.length === 0 ? (
        <Box textAlign="center" mt={5}>
          <WarehouseIcon sx={{ fontSize: 80, color: "#ccc", mb: 2 }} />
          <Typography variant="h5" color="textSecondary" gutterBottom>
            Keine Lager gefunden
          </Typography>
          <Typography variant="body2" color="textSecondary" mb={3}>
            Erstellen Sie Ihr erstes Lager, um mit der Gestaltung von Layouts zu beginnen
          </Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setOpenDialog(true)}
            sx={{ backgroundColor: "#4361ee" }}
          >
            Lager erstellen
          </Button>
        </Box>
      ) : (
        <Grid container spacing={3}>
          {warehouses.map((warehouse) => (
            <Grid item xs={12} sm={6} md={4} key={warehouse._id}>
              <Card
                sx={{
                  height: "100%",
                  display: "flex",
                  flexDirection: "column",
                  transition: "transform 0.2s",
                  "&:hover": {
                    transform: "translateY(-4px)",
                    boxShadow: 4,
                  },
                }}
              >
                <CardContent sx={{ flexGrow: 1 }}>
                  <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                    <WarehouseIcon sx={{ fontSize: 40, color: "#4361ee" }} />
                    <Chip
                      label={warehouse.isActive ? "Aktiv" : "Inaktiv"}
                      color={warehouse.isActive ? "success" : "default"}
                      size="small"
                    />
                  </Box>
                  <Typography variant="h5" component="div" gutterBottom>
                    {warehouse.name}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" mb={2}>
                    {warehouse.description || "Keine Beschreibung"}
                  </Typography>
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Abmessungen: {warehouse.dimensions.width} × {warehouse.dimensions.height}{" "}
                      {warehouse.dimensions.unit === "meters" ? "Meter" : "Fuß"}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Rasterzellengröße: {warehouse.gridCellSize} {warehouse.dimensions.unit === "meters" ? "Meter" : "Fuß"}
                    </Typography>
                  </Box>
                  {warehouse.address?.city && (
                    <Box mt={1}>
                      <Typography variant="caption" color="text.secondary">
                        📍 {warehouse.address.city}, {warehouse.address.country}
                      </Typography>
                    </Box>
                  )}
                </CardContent>
                <CardActions sx={{ justifyContent: "space-between", px: 2, pb: 2 }}>
                  <Button
                    size="small"
                    startIcon={<EditIcon />}
                    onClick={() => handleOpenDesigner(warehouse)}
                    sx={{ color: "#4361ee" }}
                  >
                    Designer
                  </Button>
                  <Box>
                    <IconButton
                      size="small"
                      onClick={() => handleViewAnalytics(warehouse)}
                      sx={{ color: "#2ecc71" }}
                    >
                      <AnalyticsIcon />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => handleDeleteWarehouse(warehouse._id, warehouse.name)}
                      sx={{ color: "#e74c3c" }}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Box>
                </CardActions>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {/* Create Warehouse Dialog */}
      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Neues Lager erstellen</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="Lagername"
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            margin="normal"
            required
          />
          <TextField
            fullWidth
            label="Beschreibung"
            value={formData.description}
            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
            margin="normal"
            multiline
            rows={2}
          />
          <Grid container spacing={2} mt={1}>
            <Grid item xs={4}>
              <TextField
                fullWidth
                label="Breite"
                type="number"
                value={formData.dimensions.width}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    dimensions: { ...formData.dimensions, width: Number(e.target.value) },
                  })
                }
              />
            </Grid>
            <Grid item xs={4}>
              <TextField
                fullWidth
                label="Höhe"
                type="number"
                value={formData.dimensions.height}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    dimensions: { ...formData.dimensions, height: Number(e.target.value) },
                  })
                }
              />
            </Grid>
            <Grid item xs={4}>
              <TextField
                fullWidth
                select
                label="Einheit"
                value={formData.dimensions.unit}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    dimensions: { ...formData.dimensions, unit: e.target.value },
                  })
                }
              >
                <MenuItem value="meters">Meter</MenuItem>
                <MenuItem value="feet">Fuß</MenuItem>
              </TextField>
            </Grid>
          </Grid>
          <TextField
            fullWidth
            label="Rasterzellengröße"
            type="number"
            value={formData.gridCellSize}
            onChange={(e) => setFormData({ ...formData, gridCellSize: Number(e.target.value) })}
            margin="normal"
            helperText="Größe jeder Rasterzelle in der ausgewählten Einheit"
          />
          <Typography variant="subtitle2" mt={2} mb={1}>
            Adresse (Optional)
          </Typography>
          <TextField
            fullWidth
            label="Straße"
            value={formData.address.street}
            onChange={(e) =>
              setFormData({
                ...formData,
                address: { ...formData.address, street: e.target.value },
              })
            }
            margin="dense"
          />
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Stadt"
                value={formData.address.city}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    address: { ...formData.address, city: e.target.value },
                  })
                }
                margin="dense"
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Postleitzahl"
                value={formData.address.postalCode}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    address: { ...formData.address, postalCode: e.target.value },
                  })
                }
                margin="dense"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)}>Abbrechen</Button>
          <Button
            onClick={handleCreateWarehouse}
            variant="contained"
            disabled={loading || !formData.name}
            sx={{ backgroundColor: "#4361ee" }}
          >
            {loading ? "Erstellen..." : "Erstellen"}
          </Button>
        </DialogActions>
      </Dialog>

      <ToastContainer position="bottom-right" autoClose={3000} />
    </Box>
  );
};

export default WarehouseList;
